import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormModelControl } from '@bcl/common-ui';
import { SnackbarService } from '@bcl/design-system';
import { Subject, takeUntil } from 'rxjs';
import { TimesheetApiService } from 'src/app/services/timesheet.service';

export interface CorrectDatesDialogData {
  visitId: number;
  currentStartDateTime: string | null;
  currentEndDateTime: string | null;
  startedAt: string | null;
  completedOn: string | null;
}

interface CorrectDatesForm {
  startDateTime: string | null;
  endDateTime: string | null;
}

@Component({
  selector: 'app-correct-dates-dialog',
  templateUrl: './correct-dates-dialog.component.html',
  styleUrls: ['./correct-dates-dialog.component.scss'],
})
export class CorrectDatesDialogComponent implements OnDestroy {
  public correctDatesForm: FormGroup<FormModelControl<CorrectDatesForm>>;
  public isLoading = false;

  private destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CorrectDatesDialogComponent>,
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: CorrectDatesDialogData,
    private timesheetApiService: TimesheetApiService,
    private snackbarService: SnackbarService
  ) {
    this.dialogRef.updateSize('500px');
    this.dialogRef.disableClose = true;

    this.correctDatesForm = fb.group({
      startDateTime: [
        data.startedAt
          ? this.convertToLocalDateTime(data.startedAt)
          : this.convertToDateTimeLocal(data.currentStartDateTime),
        Validators.required,
      ],
      endDateTime: [
        data.completedOn
          ? this.convertToLocalDateTime(data.completedOn)
          : this.convertToDateTimeLocal(data.currentEndDateTime),
        Validators.required,
      ],
    }) as FormGroup<FormModelControl<CorrectDatesForm>>;
  }

  public get hasValidationError(): boolean {
    const startDateTime = this.correctDatesForm.get('startDateTime')?.value;
    const endDateTime = this.correctDatesForm.get('endDateTime')?.value;

    if (!startDateTime || !endDateTime) {
      return false;
    }

    return new Date(startDateTime) >= new Date(endDateTime);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public save(): void {
    if (this.correctDatesForm.invalid || this.hasValidationError) {
      return;
    }

    this.isLoading = true;

    const formValue = this.correctDatesForm.value;

    const startDateTimeISO = this.convertFromDateTimeLocal(
      formValue.startDateTime!
    );
    const endDateTimeISO = this.convertFromDateTimeLocal(
      formValue.endDateTime!
    );

    this.timesheetApiService
      .correctVisitDates(this.data.visitId, startDateTimeISO!, endDateTimeISO!)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.snackbarService.open(
            'green',
            'VISIT_DAY_DETAILS.CORRECT_DATES.MESSAGES.SUCCESS'
          );
          this.dialogRef.close({
            startDateTime: startDateTimeISO,
            endDateTime: endDateTimeISO,
            startedAt: startDateTimeISO,
            completedOn: endDateTimeISO,
          });
        },
        error: (error) => {
          this.isLoading = false;
          this.snackbarService.open(
            'red',
            'VISIT_DAY_DETAILS.CORRECT_DATES.MESSAGES.ERROR'
          );
          console.error('Error correcting visit dates:', error);
        },
      });
  }

  private convertToDateTimeLocal(dateTime: string | null): string | null {
    if (!dateTime) {
      return null;
    }

    try {
      const date = new Date(dateTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return null;
    }
  }

  private convertToLocalDateTime(dateTime: string | null): string | null {
    if (!dateTime) {
      return null;
    }

    try {
      let cleanDateTime = dateTime;

      if (cleanDateTime.endsWith('Z')) {
        cleanDateTime = cleanDateTime.slice(0, -1);
      }

      const timezoneRegex = /[+-]\d{2}:\d{2}$/;
      if (timezoneRegex.test(cleanDateTime)) {
        cleanDateTime = cleanDateTime.replace(timezoneRegex, '');
      }

      const date = new Date(cleanDateTime);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return null;
    }
  }

  private convertFromDateTimeLocal(
    dateTimeLocal: string | null
  ): string | null {
    if (!dateTimeLocal) {
      return null;
    }

    try {
      const date = new Date(dateTimeLocal);
      return date.toLocaleString('sv').replace(' ', 'T');
    } catch {
      return null;
    }
  }
}
